import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, HandCoins, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import { API_BASE } from "../../lib/api";

/**
 * IntentPicker — homepage segmentation strip.
 *
 * Two top-level containers, side-by-side:
 *   1. Access VenturaZ          → holds both sub-products (Raise Funds
 *                                  for startups, Invest in Startups for
 *                                  investors) as stacked sub-cards.
 *   2. Access VenturaZ MONEY    → the copper debt vertical entry (kept
 *                                  as before).
 *
 * The debt tile stays visually distinct via the copper palette so
 * borrowers immediately understand it's a separate product ecosystem.
 */

const VENTURAZ_SUB = [
  {
    key: "equity",
    chipKey: "founders",
    accent: "navy",
    tag: "FOR FOUNDERS",
    title: "Raise Funds",
    subtitle: "Startups",
    body: "AI-matched to family offices, funds & strategics. Trust-scored profiles, verified data rooms, native e-sign.",
    signup: "/for-startups/signup",
    login: "/for-startups/login",
    icon: Rocket,
    testId: "home-intent-equity",
  },
  {
    key: "invest",
    chipKey: "investors",
    accent: "gold",
    tag: "FOR INVESTORS",
    title: "Invest in Startups",
    subtitle: "Verified Deal Access",
    body: "Hand-picked deal flow across sectors. VenturaZ Handpicked ribbon on curated deals. DD + deal rooms included.",
    signup: "/for-investors/signup",
    login: "/for-investors/login",
    icon: TrendingUp,
    testId: "home-intent-investor",
  },
];

function SubCard({ c, metric }) {
  const Icon = c.icon;
  const iconBg = c.accent === "gold" ? "bg-gold text-navy" : "bg-navy text-cream";
  const chip = c.accent === "gold" ? "text-[#8A6600] border-gold/40 bg-gold/10" : "text-navy border-navy/30 bg-navy/[0.04]";
  const btn = c.accent === "gold" ? "bg-gold text-navy hover:bg-yellow-400" : "bg-navy text-cream hover:bg-[#256E58]";
  const metricTone = c.accent === "gold" ? "bg-gold/10 border-gold/30 text-[#8A6600]" : "bg-navy/[0.05] border-navy/25 text-navy";
  return (
    <div className="relative p-4 rounded-xl border border-ink/10 bg-white hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(15,61,46,0.12)] transition-all flex flex-col" data-testid={c.testId}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-lg inline-flex items-center justify-center ${iconBg} shrink-0 shadow-[0_6px_18px_rgba(15,61,46,0.15)]`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className={`inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${chip} mb-1`}>{c.tag}</div>
          <div className="font-display text-ink text-lg leading-tight">{c.title}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">{c.subtitle}</div>
          <p className="text-[12.5px] text-ink-soft leading-relaxed mt-2">{c.body}</p>
        </div>
      </div>
      {/* Micro-metric chip — live number under each sub-card. */}
      <div className={`mt-3 self-start inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${metricTone}`} data-testid={`${c.testId}-metric`}>
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {metric}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Link
          to={c.signup}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-heading font-bold transition-colors ${btn}`}
          data-testid={`${c.testId}-signup`}
        >
          Create account <ArrowRight size={12} />
        </Link>
        <Link to={c.login} className="text-xs font-heading text-navy hover:underline" data-testid={`${c.testId}-login`}>
          Sign in →
        </Link>
      </div>
    </div>
  );
}

function VenturaZContainer({ chips }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-navy/10 bg-gradient-to-br from-white via-white to-cream/40 p-5 md:p-6"
      data-testid="home-intent-venturaz"
    >
      {/* Ambient corner tints */}
      <div className="pointer-events-none absolute -top-16 -left-16 w-56 h-56 rounded-full opacity-25 bg-navy" style={{ filter: "blur(56px)" }} />
      <div className="pointer-events-none absolute -bottom-20 -right-20 w-64 h-64 rounded-full opacity-25 bg-gold" style={{ filter: "blur(64px)" }} />

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 bg-navy text-cream text-[10px] font-mono uppercase tracking-[0.24em] font-bold px-2.5 py-1 rounded-full">
            <Sparkles size={11} className="text-gold" /> Access VenturaZ
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Equity · one platform</span>
        </div>
        <div className="font-display text-2xl md:text-3xl text-ink leading-tight mb-1">
          Raise, or deploy — both live here.
        </div>
        <p className="text-sm text-ink-soft mb-4 max-w-xl">
          Two roles on the same platform. Founders build a verified capital passport. Investors get curated,
          diligence-backed deal flow.
        </p>

        {/* Sub-cards side-by-side so both entry points stay above the fold. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {VENTURAZ_SUB.map((c) => <SubCard key={c.key} c={c} metric={chips?.[c.chipKey]} />)}
        </div>
      </div>
    </div>
  );
}

function DebtContainer({ metric }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-[#B8860B]/30 group hover:-translate-y-0.5 transition-all"
      style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 45%, #7A4E1D 100%)" }}
      data-testid="home-intent-debt"
    >
      {/* Ambient copper glow */}
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #B8860B 0%, transparent 60%)" }} />

      <div className="relative p-5 md:p-6 flex flex-col h-full">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.24em] font-bold px-2.5 py-1 rounded-full shadow-[0_4px_16px_rgba(233,196,106,0.35)]" data-testid="home-intent-debt-chip">
            <Sparkles size={11} /> VenturaZ MONEY · Debt Ecosystem
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#E9C46A]/70">A different world</span>
        </div>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg inline-flex items-center justify-center bg-gold text-[#1F1408] shadow-[0_8px_24px_rgba(233,196,106,0.35)] shrink-0">
            <HandCoins size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[#FFF3D9] text-2xl md:text-3xl leading-tight mb-1">
              Access VenturaZ Money
            </div>
            <p className="text-[#FFF3D9]/85 text-[13px] md:text-sm leading-relaxed mb-3">
              India&apos;s honest loan advisor — for individuals AND businesses. Compare offers from 85+ banks &amp; NBFCs,
              free CIBIL worth ₹999, zero platform fee. <b>Advisor, never lender.</b>
            </p>
            {/* Quick surface links */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono uppercase tracking-widest text-[#E9C46A]/90 mb-3">
              <Link to="/venturaz-money/products" className="hover:text-gold hover:underline" data-testid="home-intent-debt-link-products">15 products →</Link>
              <Link to="/venturaz-money/banks" className="hover:text-gold hover:underline" data-testid="home-intent-debt-link-banks">114 lenders →</Link>
              <Link to="/venturaz-money/become-partner" className="hover:text-gold hover:underline" data-testid="home-intent-debt-link-partner">Become a partner →</Link>
            </div>
            {/* Live-number chip mirrors the equity sub-card metrics. */}
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-gold/40 bg-gold/10 text-[#E9C46A]" data-testid="home-intent-debt-metric">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {metric}
            </div>
          </div>
        </div>
        {/* CTA cluster — pushed to the bottom */}
        <div className="mt-auto pt-2 flex items-center gap-3">
          <Link
            to="/money"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gold text-[#1F1408] text-sm font-heading font-bold hover:bg-yellow-400 transition-colors shadow-[0_8px_24px_rgba(233,196,106,0.28)]"
            data-testid="home-intent-debt-explore"
          >
            Explore VenturaZ Money <ArrowRight size={14} />
          </Link>
          <Link
            to="/money/login"
            className="text-[#FFF3D9] text-sm font-heading hover:text-gold underline-offset-2 hover:underline"
            data-testid="home-intent-debt-login"
          >
            Sign in →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function IntentPicker() {
  const [chips, setChips] = useState({
    founders: "214 founders raising · ₹1,240 Cr live",
    investors: "380 investors · 214 term sheets facilitated",
    debt: "50,000+ partners · ₹1,240 Cr disbursed FY",
  });
  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/public/hero-pulse`)
      .then((r) => r.json())
      .then((d) => { if (mounted && d?.intent_chips) setChips((c) => ({ ...c, ...d.intent_chips })); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  return (
    <section className="relative py-14 md:py-20 bg-cream" data-testid="home-intent-picker">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8 md:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-navy mb-2">Start here · Pick your surface</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">Access VenturaZ.</h2>
            <p className="text-sm text-ink-soft mt-2 max-w-2xl">
              Two roles on the same platform. Founders build a verified capital passport. Investors get curated,
              diligence-backed deal flow. Pick where you&apos;re starting.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 items-stretch" data-testid="home-intent-picker-row">
          <VenturaZContainer chips={chips} />
        </div>

        {/* Low-key link to the sister product — kept out of the primary
            picker so equity remains the homepage's dominant story. */}
        <div className="mt-6 text-center">
          <Link
            to="/money"
            className="inline-flex items-center gap-1 text-[12px] font-heading text-ink-muted hover:text-[#8A6600] underline-offset-4 hover:underline"
            data-testid="home-intent-money-quiet-link"
          >
            Need funding, not investing? → VenturaZ Money
          </Link>
        </div>
      </div>
    </section>
  );
}
